import { inject } from "@angular/core";
import {
  ActivatedRouteSnapshot,
  Router,
  RouterStateSnapshot,
} from "@angular/router";
import { JwtHelperService } from "@auth0/angular-jwt";
import { AuthService } from "src/services/auth/auth.service";
import Swal from "sweetalert2";

// Constantes pour la configuration de l'alerte
const ACCESS_DENIED_ALERT = {
  title: "Accès refusé",
  errorText: "Votre session a expiré. Veuillez vous reconnecter.",
  roleErrorText: "Vous n'avez pas les droits nécessaires.",
  icon: "error" as const,
  showCancelButton: false,
  confirmButtonColor: "#006481",
  color: "#006481",
  iconColor: "#E40E20",
  confirmButtonText: "FERMER",
  allowOutsideClick: false,
};

export const RolesGuard = (
  route: ActivatedRouteSnapshot,
  state: RouterStateSnapshot
): boolean => {
  const authService = inject(AuthService);
  const jwtHelper = inject(JwtHelperService);
  const router = inject(Router);

  // Récupérer les rôles nécessaires depuis les données de la route
  const requiredRoles = route.data["roles"] || [];

  // Vérifier la présence et la validité du token
  const token = authService.getToken();
  if (!token || jwtHelper.isTokenExpired()) {
    showAccessDeniedAlert(ACCESS_DENIED_ALERT.errorText, () => {
      router.navigate(["/login"], { queryParams: { returnUrl: state.url } });
    });
    return false;
  }

  // Vérifier les rôles
  const userRoles = getCurrentUserRoles(jwtHelper);
  if (hasRequiredRoles(userRoles, requiredRoles)) {
    return true;
  }

  showAccessDeniedAlert(ACCESS_DENIED_ALERT.roleErrorText);
  return false;
};

// Fonctions utilitaires
function getCurrentUserRoles(jwtHelper: JwtHelperService): string[] {
  const payload = jwtHelper.decodeToken();
  return payload?.role ? [payload.role] : [];
}

function hasRequiredRoles(
  userRoles: string[],
  requiredRoles: string[]
): boolean {
  if (requiredRoles.length === 0) return true; // Aucun rôle spécifié = accès autorisé
  return userRoles.some((role) => requiredRoles.includes(role));
}

function showAccessDeniedAlert(text: string, onConfirm?: () => void): void {
  Swal.fire({
    ...ACCESS_DENIED_ALERT,
    text,
  }).then((result) => {
    if (result.isConfirmed && onConfirm) {
      onConfirm();
    }
  });
}
